import type { RealtimeClientMessage, RealtimeServerMessage } from "@tcr/contracts";

export type LinkState = "idle" | "connecting" | "open" | "reconnecting" | "closed";
export type RealtimeEvent = Extract<RealtimeServerMessage, { type: "event" }>;

export interface RealtimeManagerOptions {
  /** POST /realtime/tickets → one-time ticket. */
  fetchTicket: () => Promise<string>;
  socketUrl: (ticket: string) => string;
  WebSocketImpl?: typeof WebSocket;
  initialBackoffMs?: number;
  maxBackoffMs?: number;
  heartbeatMs?: number;
  /** Close and reconnect when a ping is unanswered for this long. */
  pongTimeoutMs?: number;
  random?: () => number;
}

type Listener<T> = (v: T) => void;

/**
 * Single browser realtime connection.
 *  - fetches a one-time ticket per connection attempt,
 *  - reconnects with jittered exponential backoff,
 *  - refcounts topics so components can subscribe/unsubscribe independently,
 *  - resubscribes every live topic after a reconnect,
 *  - keeps an application-level heartbeat (ping/pong) and measures RTT.
 */
export class RealtimeManager {
  private ws: WebSocket | null = null;
  private readonly refs = new Map<string, number>();
  private state: LinkState = "idle";
  private attempt = 0;
  private running = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private readonly pings = new Map<string, { sentAt: number; resolve: (rtt: number) => void }>();
  private pingSeq = 0;
  private readonly stateListeners = new Set<Listener<LinkState>>();
  private readonly messageListeners = new Set<Listener<RealtimeServerMessage>>();
  private readonly rttListeners = new Set<Listener<number>>();
  private readonly opts: Required<Omit<RealtimeManagerOptions, "WebSocketImpl">> & { WebSocketImpl?: typeof WebSocket };

  constructor(opts: RealtimeManagerOptions) {
    this.opts = {
      initialBackoffMs: 500,
      maxBackoffMs: 15_000,
      heartbeatMs: 10_000,
      pongTimeoutMs: 8_000,
      random: Math.random,
      ...opts,
    };
  }

  get linkState(): LinkState {
    return this.state;
  }

  get isOpen(): boolean {
    return this.state === "open" && this.ws?.readyState === 1;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.attempt = 0;
    void this.connect();
  }

  stop(): void {
    this.running = false;
    this.clearTimers();
    const ws = this.ws;
    this.ws = null;
    if (ws) {
      ws.onclose = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.onopen = null;
      try {
        ws.close(1000, "client stop");
      } catch {
        /* ignore */
      }
    }
    this.setState("idle");
  }

  /** Subscribe to a topic; returns the matching unsubscribe. Safe to call while disconnected. */
  subscribe(topic: string): () => void {
    const n = this.refs.get(topic) ?? 0;
    this.refs.set(topic, n + 1);
    if (n === 0 && this.isOpen) this.rawSend({ type: "subscribe", topics: [topic] });
    let done = false;
    return () => {
      if (done) return;
      done = true;
      const c = (this.refs.get(topic) ?? 1) - 1;
      if (c <= 0) {
        this.refs.delete(topic);
        if (this.isOpen) this.rawSend({ type: "unsubscribe", topics: [topic] });
      } else {
        this.refs.set(topic, c);
      }
    };
  }

  topics(): string[] {
    return [...this.refs.keys()];
  }

  /** Send a client message. Returns false (and drops it) when the socket isn't open — never queues. */
  send(msg: RealtimeClientMessage): boolean {
    if (!this.isOpen) return false;
    return this.rawSend(msg);
  }

  /** Round-trip time in ms, or rejects after pongTimeoutMs. */
  ping(): Promise<number> {
    return new Promise((resolve, reject) => {
      if (!this.isOpen) return reject(new Error("not connected"));
      const nonce = `p${++this.pingSeq}-${Date.now()}`;
      this.pings.set(nonce, { sentAt: performance.now(), resolve });
      this.rawSend({ type: "ping", nonce });
      setTimeout(() => {
        if (this.pings.delete(nonce)) reject(new Error("pong timeout"));
      }, this.opts.pongTimeoutMs);
    });
  }

  onState(fn: Listener<LinkState>): () => void {
    this.stateListeners.add(fn);
    return () => this.stateListeners.delete(fn);
  }
  onMessage(fn: Listener<RealtimeServerMessage>): () => void {
    this.messageListeners.add(fn);
    return () => this.messageListeners.delete(fn);
  }
  onRtt(fn: Listener<number>): () => void {
    this.rttListeners.add(fn);
    return () => this.rttListeners.delete(fn);
  }

  // ───────────── internals ─────────────

  private setState(s: LinkState) {
    if (this.state === s) return;
    this.state = s;
    for (const l of this.stateListeners) l(s);
  }

  private rawSend(msg: RealtimeClientMessage): boolean {
    try {
      this.ws?.send(JSON.stringify(msg));
      return true;
    } catch {
      return false;
    }
  }

  private clearTimers() {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.reconnectTimer = null;
    this.heartbeatTimer = null;
    this.pings.clear();
  }

  /** Full-jitter exponential backoff. */
  backoffDelay(attempt: number): number {
    const cap = Math.min(this.opts.maxBackoffMs, this.opts.initialBackoffMs * 2 ** attempt);
    return Math.round(cap / 2 + (cap / 2) * this.opts.random());
  }

  private scheduleReconnect() {
    if (!this.running) return;
    this.setState("reconnecting");
    const delay = this.backoffDelay(this.attempt++);
    this.reconnectTimer = setTimeout(() => void this.connect(), delay);
  }

  private async connect() {
    if (!this.running) return;
    this.setState(this.attempt === 0 ? "connecting" : "reconnecting");
    let ticket: string;
    try {
      ticket = await this.opts.fetchTicket();
    } catch {
      this.scheduleReconnect();
      return;
    }
    if (!this.running) return;
    const Impl = this.opts.WebSocketImpl ?? WebSocket;
    let ws: WebSocket;
    try {
      ws = new Impl(this.opts.socketUrl(ticket));
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      if (this.ws !== ws) return;
      this.attempt = 0;
      this.setState("open");
      const topics = this.topics();
      // Resubscribe everything that is still referenced (chunks of 100 per contract).
      for (let i = 0; i < topics.length; i += 100) this.rawSend({ type: "subscribe", topics: topics.slice(i, i + 100) });
      this.heartbeatTimer = setInterval(() => {
        this.ping().catch(() => {
          // Unanswered heartbeat: the link is dead even if TCP hasn't noticed.
          if (this.ws === ws) ws.close(4000, "heartbeat timeout");
        });
      }, this.opts.heartbeatMs);
    };
    ws.onmessage = (ev) => {
      let msg: RealtimeServerMessage;
      try {
        msg = JSON.parse(typeof ev.data === "string" ? ev.data : "") as RealtimeServerMessage;
      } catch {
        return;
      }
      if (msg.type === "pong") {
        const p = this.pings.get(msg.nonce);
        if (p) {
          this.pings.delete(msg.nonce);
          const rtt = performance.now() - p.sentAt;
          p.resolve(rtt);
          for (const l of this.rttListeners) l(rtt);
        }
      }
      for (const l of this.messageListeners) l(msg);
    };
    ws.onerror = () => {
      /* onclose follows */
    };
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.ws = null;
      if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
      this.pings.clear();
      if (this.running) this.scheduleReconnect();
      else this.setState("closed");
    };
  }
}
